/**
 * A2 合规风险合成（前端生产入口）。
 *
 * 流程：
 *   project facts → selectRelevantTopics (A1) → buildPrompt
 *   → POST /api/ai/score → parseAiResponse → validateAndEnrichFindings
 *   → 输出结构化 result
 *
 * 内部不抛 — 任何错误进 result._error 字段，调用方可降级 UI。
 */

import { RD_BASE as AI_BASE } from './config';
import { selectRelevantTopics } from './complianceKnowledge';
import {
  buildPrompt,
  buildMessages,
  parseAiResponseWithPrefill,
  validateAndEnrichFindings,
} from './synthesizeComplianceRiskCore';

const DEFAULT_MODEL = 'claude-opus-4-7';
const DEFAULT_MAX_TOKENS = 1800;

/**
 * @param {object} projectFacts        见 complianceKnowledge.selectRelevantTopics
 * @param {object} [options]
 * @param {string[]} [options.onlyJurisdictions]
 * @param {string} [options.model]
 * @param {number} [options.maxTokens]
 * @param {function} [options.fetchFn] 可注入 fetch（测试用）
 */
export async function synthesizeComplianceRisk(projectFacts, options = {}) {
  const {
    onlyJurisdictions,
    model = DEFAULT_MODEL,
    maxTokens = DEFAULT_MAX_TOKENS,
    fetchFn = (typeof fetch !== 'undefined' ? fetch : null),
  } = options;

  const selectedTopics = selectRelevantTopics(projectFacts, { onlyJurisdictions });

  if (selectedTopics.length === 0) {
    return {
      overall_severity: 'low',
      summary: '未匹配任何 HK 监管 topic — 项目当前特征不触发已收录的合规要求。',
      findings: [],
      invalid_findings: [],
      topics_considered: [],
      _meta: { model: null, prompt_chars: 0, generated_at: new Date().toISOString() },
    };
  }

  const prompt = buildPrompt(projectFacts, selectedTopics);
  const messages = buildMessages(projectFacts, selectedTopics);

  if (!fetchFn) {
    return {
      overall_severity: 'low',
      summary: '',
      findings: [],
      invalid_findings: [],
      topics_considered: selectedTopics.map(h => ({
        jurisdiction: h.topic.jurisdiction,
        topic: h.topic.topic,
        severity_if_breached: h.topic.severity_if_breached,
        match_reason: h.matchReason,
      })),
      _error: 'no fetch available',
      _meta: { model: null, prompt_chars: prompt.length, generated_at: new Date().toISOString() },
    };
  }

  let raw;
  try {
    const res = await fetchFn(`${AI_BASE}/api/ai/score`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        model,
        max_tokens: maxTokens,
        messages,
      }),
    });
    if (!res || !res.ok) {
      return {
        overall_severity: 'low',
        summary: '',
        findings: [],
        invalid_findings: [],
        topics_considered: selectedTopics.map(h => ({
          jurisdiction: h.topic.jurisdiction,
          topic: h.topic.topic,
          severity_if_breached: h.topic.severity_if_breached,
          match_reason: h.matchReason,
        })),
        _error: `ai api ${res ? res.status : 'no response'}`,
        _meta: { model, prompt_chars: prompt.length, generated_at: new Date().toISOString() },
      };
    }
    raw = await res.json();
    // Q60
    if (raw._fallback) console.warn(`[compliance-ai] ⚠️ DeepSeek 兜底, 原因: ${raw._fallback_reason || '未知'}`);
    else console.log(`[compliance-ai] ✅ ${raw.model || 'claude'} OK`);
  } catch (err) {
    return {
      overall_severity: 'low',
      summary: '',
      findings: [],
      invalid_findings: [],
      topics_considered: selectedTopics.map(h => ({
        jurisdiction: h.topic.jurisdiction,
        topic: h.topic.topic,
        severity_if_breached: h.topic.severity_if_breached,
        match_reason: h.matchReason,
      })),
      _error: `fetch failed: ${err.message}`,
      _meta: { model, prompt_chars: prompt.length, generated_at: new Date().toISOString() },
    };
  }

  const txt = Array.isArray(raw.content)
    ? raw.content.map(b => (b.type === 'text' ? b.text : '')).join('')
    : (typeof raw === 'string' ? raw : '');
  const parsed = parseAiResponseWithPrefill(txt);

  if (!parsed.ok) {
    return {
      overall_severity: 'low',
      summary: '',
      findings: [],
      invalid_findings: [],
      topics_considered: selectedTopics.map(h => ({
        jurisdiction: h.topic.jurisdiction,
        topic: h.topic.topic,
        severity_if_breached: h.topic.severity_if_breached,
        match_reason: h.matchReason,
      })),
      _error: `parse: ${parsed.error}`,
      _meta: { model, prompt_chars: prompt.length, generated_at: new Date().toISOString() },
    };
  }

  const result = validateAndEnrichFindings(parsed.data, selectedTopics);
  return {
    ...result,
    _meta: { model, prompt_chars: prompt.length, generated_at: new Date().toISOString() },
  };
}
